/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, User as UserIcon, LogOut, CalendarCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import PartnersMenu from './PartnersMenu';
import LanguageSelector from './LanguageSelector';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { tenant, isMainDomain } = useTenant();
  const { user, isAuthenticated, signOut } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="surface border-x-0 border-t-0 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              {tenant?.logoUrl ? (
                <img src={tenant.logoUrl} alt={tenant.displayName} className="h-9 w-auto" />
              ) : (
                <Car size={26} style={{ color: 'var(--color-primary)' }} />
              )}
              <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                {tenant?.displayName || 'My E-Z Wheels'}
              </span>
            </Link>
            {/* Tenant home link (replaces the old "storefront" badge) → the tenant's main page. */}
            {!isMainDomain && tenant && (
              <Link to="/" className="text-sm font-medium hover:underline" style={{ color: 'var(--color-primary)' }}>
                {t('nav.home', 'Home')}
              </Link>
            )}
          </div>

          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <Link to="/" className="hidden sm:inline hover:underline">{t('nav.search')}</Link>
            {isAuthenticated && (
              <Link to="/my-bookings" className="flex items-center gap-1 hover:underline">
                <CalendarCheck size={15} />
                <span className="hidden sm:inline">{t('nav.myBookings', 'My bookings')}</span>
              </Link>
            )}
            {isMainDomain && <PartnersMenu />}
            <LanguageSelector />
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <UserIcon size={15} /> {user?.firstName || user?.email}
                </span>
                <button onClick={signOut} className="opacity-60 hover:opacity-100" title={t('nav.signOut')}>
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowLogin(true)} className="btn-outline py-1.5">
                {t('nav.signIn')}
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="surface border-x-0 border-b-0 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 text-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-center sm:text-left">
          <span className="opacity-70">{tenant?.displayName || 'My E-Z Wheels'}</span>
          <nav className="flex items-center justify-center gap-4">
            <Link to="/about" className="opacity-70 hover:opacity-100 hover:underline">{t('nav.about', 'About')}</Link>
            <Link to="/terms" className="opacity-70 hover:opacity-100 hover:underline">{t('nav.terms', 'Terms & Conditions')}</Link>
            <Link to="/contact" className="opacity-70 hover:opacity-100 hover:underline">{t('nav.contact', 'Contact')}</Link>
          </nav>
          <span className="opacity-70">{t('footer.poweredBy')}</span>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-6 flex flex-col gap-1 text-xs opacity-60 text-center sm:text-left">
          <span>{t('footer.copyright', '© 2026 Aegis AO Soft LLC. All rights reserved.')}</span>
          <span>
            {t(
              'footer.license',
              'Aegis AG Soft LLC and all other users use this software solely under a non-exclusive, revocable license granted by Aegis AO Soft LLC.'
            )}
          </span>
        </div>
      </footer>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
}
